import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {Button, Checkbox, GearIcon, PictureIcon, TextInput} from '@taopix/taopix-design-system';
import {useTaopix} from '../../Context/TaopixContext';
import classNames from 'classnames';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import ClearImages from './Mods/ClearImages';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';

export type AssetsPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

const AssetsPalette = ({sectionKey, item}: AssetsPaletteProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation(['toolpalette', 'stubar']);

  const validation = {
    allowpicturejpegrgb: true,
    allowpicturejpegcmyk: true,
    allowpicturejpeggray: true,
    allowpicturetiffrgb: true,
    allowpicturetiffcmyk: true,
    allowpicturetiffgray: true,
    allowpicturepng: true,
  };

  const onChange = (
    isChecked: boolean | undefined,
    event: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const stateManager = app.core.get('stateManager');
    const layout = stateManager.getLayout();
    stateManager.setLayout({
      ...layout,
      features: {
        ...layout.features,
        [key]: undefined !== isChecked ? isChecked : Number(event.target.value) || 0,
      },
    });
  };

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'sm:pb-md',
    'sm:w-[300rem]',
    'sm:max-w-full',
    'sm:mx-auto',
    'overflow-y-auto'
  );

  const pageOptionButtonProps: ButtonProps = {
    buttonStyle: 'primary',
    size: ui.state.isSmallScreen ? 'medium' : 'small',
    labelAlignment: 'left',
  };

  useEffect(() => {
    app.core.get('stateManager').addModule('clearImages', new ClearImages());
  }, []);

  return (
    <ToolPaletteSection
      subSection
      buttonClasses={sectionButtonClasses}
      subSectionPanelType="slide-in"
      sectionKey={sectionKey}
      title={t('str_LabelAssets')}
      buttonIcon={<GearIcon />}
      buttonLayout={'horizontal'}
      id="devAssetsPalette"
    >
      <div className={wrapperClasses}>
        <div className={'lg:px-md lg:flex lg:flex-col lg:pt-md lg:space-y-sm'}>
          <Button
            label={t('str_LabelRemovePictureBox', {
              ns: 'stubar',
            })}
            startIcon={<PictureIcon />}
            {...pageOptionButtonProps}
            onClick={() => app.core.get('stateManager').call('clearImages')}
            labelLines={1}
          />
          <TextInput
            label={'Ai Mode'}
            id={'aimode'}
            name={'aimode'}
            defaultValue={app.core.get('stateManager').getLayout().features.aimode}
            onChange={e => onChange(undefined, e, 'aimode')}
            className={'w-full'}
          />
          {Object.keys(validation).map(k => {
            return (
              <Checkbox
                key={k}
                id={k}
                label={k}
                onChange={(isChecked, e) => onChange(isChecked, e, k)}
                defaultChecked={validation[k]}
              />
            );
          })}
        </div>
      </div>
    </ToolPaletteSection>
  );
};

export default AssetsPalette;
